import { useLanguage } from "@/lib/i18n";
import { LegalPageLayout } from "./LegalPageLayout";

export default function TermsPage() {
  const { t, language } = useLanguage();

  return (
    <LegalPageLayout titleKey="legal.terms.title" seoDescriptionKey="legal.terms.seoDescription" pagePath="/terms">
      <h1 className="text-2xl md:text-3xl font-bold mb-2">{t('legal.terms.title')}</h1>
      <p className="text-sm text-muted-foreground mb-8">{t('legal.terms.effectiveDate')}</p>

      {/* Section 1 */}
      <Section title={t('legal.terms.s1.title')}>
        <P>{t('legal.terms.s1.1')}</P>
        <P>{t('legal.terms.s1.2')}</P>
        <P>{t('legal.terms.s1.3')}</P>
        <P>{t('legal.terms.s1.4')}</P>
        <P>{t('legal.terms.s1.5')}</P>
        <P>{t('legal.terms.s1.6')}</P>
        <P>{t('legal.terms.s1.7')}</P>
        <P>{t('legal.terms.s1.8')}</P>
        <P>{t('legal.terms.s1.9')}</P>
      </Section>

      {/* Section 2 */}
      <Section title={t('legal.terms.s2.title')}>
        <Pre>{t('legal.terms.s2.content')}</Pre>
      </Section>

      {/* Section 3 */}
      <Section title={t('legal.terms.s3.title')}>
        <P>{t('legal.terms.s3.1')}</P>
        <P>{t('legal.terms.s3.2')}</P>
        <P>{t('legal.terms.s3.3')}</P>
      </Section>

      {/* Section 4 */}
      <Section title={t('legal.terms.s4.title')}>
        <P>{t('legal.terms.s4.1')}</P>
        <P>{t('legal.terms.s4.2')}</P>
        <P>{t('legal.terms.s4.3')}</P>
        <P>{t('legal.terms.s4.4')}</P>
        <P>{t('legal.terms.s4.5')}</P>
      </Section>

      {/* Section 5 */}
      <Section title={t('legal.terms.s5.title')}>
        <P>{t('legal.terms.s5.1')}</P>
        <P>{t('legal.terms.s5.2')}</P>
        <P>{t('legal.terms.s5.3')}</P>
        <P>{t('legal.terms.s5.4')}</P>
        <P>{t('legal.terms.s5.5')}</P>
        <P>{t('legal.terms.s5.6')}</P>
        <P>{t('legal.terms.s5.7')}</P>
        <P>{t('legal.terms.s5.8')}</P>
      </Section>

      {/* Section 6 */}
      <Section title={t('legal.terms.s6.title')}>
        <P>{t('legal.terms.s6.content')}</P>
      </Section>

      {/* Section 7 */}
      <Section title={t('legal.terms.s7.title')}>
        <P>{t('legal.terms.s7.1')}</P>
        <P>{t('legal.terms.s7.2')}</P>
        <P>{t('legal.terms.s7.5')}</P>
        <P>{t('legal.terms.s7.10')}</P>
      </Section>

      {/* Section 8 */}
      <Section title={t('legal.terms.s8.title')}>
        <P>{t('legal.terms.s8.1')}</P>
        <P>{t('legal.terms.s8.2')}</P>
        <P>{t('legal.terms.s8.3')}</P>
        <P>{t('legal.terms.s8.10')}</P>
      </Section>

      {/* Section 9 */}
      <Section title={t('legal.terms.s9.title')}>
        <P>{t('legal.terms.s9.content')}</P>
      </Section>

      {/* Section 10 */}
      <Section title={t('legal.terms.s10.title')}>
        <P>{t('legal.terms.s10.content')}</P>
      </Section>

      {/* Section 11 */}
      <Section title={t('legal.terms.s11.title')}>
        <P>{t('legal.terms.s11.content')}</P>
      </Section>

      {/* Section 12 */}
      <Section title={t('legal.terms.s12.title')}>
        <P>{t('legal.terms.s12.content')}</P>
      </Section>

      {/* Section 13 */}
      <Section title={t('legal.terms.s13.title')}>
        <P>{t('legal.terms.s13.3')}</P>
        <P>{t('legal.terms.s13.3.1')}</P>
      </Section>

      {/* Part B – Hebrew clarification (only shown in Hebrew) */}
      {language === 'he' && (
        <div className="mt-12 pt-8 border-t border-border/50">
          <h2 className="text-xl font-bold mb-4">{t('legal.terms.partB.title')}</h2>
          <p className="text-sm text-primary font-medium bg-primary/5 rounded-lg p-4 mb-6">
            {t('legal.terms.partB.notice')}
          </p>

          <Section title={t('legal.terms.partB.s1.title')}>
            <Pre>{t('legal.terms.partB.s1.content')}</Pre>
          </Section>
          <Section title={t('legal.terms.partB.s2.title')}>
            <P>{t('legal.terms.partB.s2.content')}</P>
          </Section>
          <Section title={t('legal.terms.partB.s3.title')}>
            <Pre>{t('legal.terms.partB.s3.content')}</Pre>
          </Section>
          <Section title={t('legal.terms.partB.s4.title')}>
            <Pre>{t('legal.terms.partB.s4.content')}</Pre>
          </Section>
          <Section title={t('legal.terms.partB.s5.title')}>
            <Pre>{t('legal.terms.partB.s5.content')}</Pre>
          </Section>
          <Section title={t('legal.terms.partB.s6.title')}>
            <Pre>{t('legal.terms.partB.s6.content')}</Pre>
          </Section>
          <Section title={t('legal.terms.partB.s7.title')}>
            <P>{t('legal.terms.partB.s7.content')}</P>
          </Section>

          <div className="mt-6 p-4 bg-muted/50 rounded-lg">
            <Pre>{t('legal.terms.partB.operator')}</Pre>
          </div>
        </div>
      )}
    </LegalPageLayout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="legal-heading text-lg font-semibold mb-3">{title}</h2>
      {children}
    </section>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="legal-content text-sm text-muted-foreground leading-relaxed mb-2">{children}</p>;
}

function Pre({ children }: { children: React.ReactNode }) {
  return <p className="legal-content text-sm text-muted-foreground leading-relaxed mb-2 whitespace-pre-line">{children}</p>;
}
